import { createSlice } from '@reduxjs/toolkit';
import endPoints from '../api/endpoint';
import store, { RootState } from './store';
import {
  getApiCall,
  multiGetApiCallWithDispatch,
  postApiCall,
  putApiCall,
} from '../api/methods';
import { notify } from '../Utils/toastify';
import { StudioState } from '../types';
import { setCommonDataLoader } from './commonSlice';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../Utils/firebaseConfig';
import { ANALYTICS_EVENTS } from '../helpers/analyticsEvents';

const initialState: StudioState = {
  commission: [],
  loading: false,
  error: false,
  vendorsLists: {},
  studioSpendingGraph: {},
  type: 0,
  studio: {},
  pageNo: 1,
  studiolist: {},
  studiolistactive: {},
  studiolistinactive: {},
  studiolistupcoming: {},
  graphLoader: false,
  allStudiolist: {},
  studioSpendingLists: [],
  studioLocations: {},
  studioDetail: {},
  studioProductionLocations: {},
  studioOrders: {},
  studioFormData: {},
  contacts: [],
  entityDeps: [],
  studioRoleList: [],
  roleDetails: {},
  entityCount: {},
  contactDetails: {},
};
const studioSlice = createSlice({
  name: 'studio',
  initialState,
  reducers: {
    setStudioList: (state, action) => {
      const { payload }: any = action;
      state.studio = payload;
    },
    setProductionListings: (state, action) => {
      const { payload }: any = action;
      state.studiolist = payload;
    },
    setStudioListingsActive: (state, action) => {
      const { payload }: any = action;
      state.studiolistactive = payload;
    },
    setStudioListingsInactive: (state, action) => {
      const { payload }: any = action;
      state.studiolistinactive = payload;
    },
    setStudioListingsUpcoming: (state, action) => {
      const { payload }: any = action;
      state.studiolistupcoming = payload;
    },
    setTabValue: (state, action) => {
      // Action to set the current tab
      state.type = action.payload;
    },
    setPageNo: (state, action) => {
      const { payload }: any = action;
      state.pageNo = payload;
    },
    setStudioSpendingGraph: (state, action) => {
      const { payload }: any = action;
      state.studioSpendingGraph = payload;
    },
    setSpendingGraphLoader: (state, action) => {
      const { payload }: any = action;
      state.graphLoader = payload;
    },
    setAllStudioLists: (state, action) => {
      const { payload }: any = action;
      state.allStudiolist = payload;
    },
    setAllStudioSpendingList: (state, action) => {
      const { payload }: any = action;
      state.studioSpendingLists = payload;
    },
    setStudioLocation: (state, action) => {
      const { payload }: any = action;
      state.studioLocations = payload;
    },
    setStudioProductionLocation: (state, action) => {
      const { payload }: any = action;
      state.studioProductionLocations = payload;
    },
    setStudioDetail: (state, action) => {
      const { payload }: any = action;
      state.studioDetail = payload;
    },
    setStudioOrders: (state, action) => {
      const { payload }: any = action;
      state.studioOrders = payload;
    },
    setLoading: (state, action) => {
      const { payload }: any = action;
      state.loading = payload;
    },
    setStudioFormDetails: (state, action) => {
      const { payload }: any = action;
      state.studioFormData = payload;
    },
    setStudioContactsList: (state, action) => {
      const { payload }: any = action;
      state.contacts = payload;
    },
    setEntityDeps: (state, action) => {
      const { payload }: any = action;
      state.entityDeps = payload;
    },
    setStudioRoleList: (state, action) => {
      const { payload }: any = action;
      state.studioRoleList = payload;
    },
    setRoleDetails: (state, action) => {
      const { payload }: any = action;
      state.roleDetails = payload;
    },
    setEntityCount: (state, action) => {
      const { payload }: any = action;
      state.entityCount = payload;
    },
    setContactDetails: (state, action) => {
      const { payload }: any = action;
      state.contactDetails = payload;
    },
  },
});

export const {
  setStudioList,
  setStudioDetail,
  setTabValue,
  setPageNo,
  setStudioListingsActive,
  setStudioListingsInactive,
  setStudioListingsUpcoming,
  setStudioSpendingGraph,
  setSpendingGraphLoader,
  setAllStudioLists,
  setAllStudioSpendingList,
  setStudioLocation,
  setStudioProductionLocation,
  setLoading,
  setStudioOrders,
  setStudioFormDetails,
  setStudioContactsList,
  setEntityDeps,
  setStudioRoleList,
  setEntityCount,
  setRoleDetails,
  setContactDetails,
} = studioSlice.actions;

export default studioSlice.reducer;

export const getStudioList = (
  query = '?page=1&limit=10',
  selectedstudioId: any,
  status: any,
  cb?: any,
) => {
  const { dispatch } = store;
  dispatch(setCommonDataLoader(true));

  // Build the API endpoint with the query and studioId
  let apiendpoint =
    `${endPoints.studio.studiolist}${query}` +
    `&studioId=` +
    `${selectedstudioId}`;

  // Add status to the endpoint if it's not 0 or null
  if (status && status !== 0) {
    apiendpoint += '&status=' + `${status}`;
  }

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        // Check the status value and dispatch the appropriate action
        if (status === 'ACTIVE') {
          dispatch(setStudioListingsActive(data.data));
        } else if (status === 'COMPLETED') {
          dispatch(setStudioListingsInactive(data.data));
        } else if (status === 'INACTIVE') {
          dispatch(setStudioListingsUpcoming(data.data));
        } else {
          // If status is null or 0, dispatch the general studio list action
          dispatch(setStudioList(data.data));
        }

        // Callback after dispatch
        cb(data.data);

        // Update the page number (total production count)
        dispatch(setPageNo(data.data.productionCount));
      }

      // Stop loading indicator
      dispatch(setCommonDataLoader(false));
    },
    (e: any) => {
      // Handle error, stop loading
      dispatch(setCommonDataLoader(false));
    },
  );
};

export const getStudioSpendingGraph = (
  studioId: string,
  id: string,
  range: string,
  productionId?: string,
  cb?: any,
) => {
  const { dispatch } = store;
  dispatch(setCommonDataLoader(true));
  dispatch(setSpendingGraphLoader(true));
  setStudioSpendingGraph({});

  // Build the API endpoint with the query and studioId
  // let apiendpoint = endPoints.production.dashboardGraph(type, id, range)

  let apiendpoint = endPoints.dashBoard.spendingGraph;

  if (id === 'allProductions') {
    apiendpoint +=
      `?filterType=production&entityType=studio&entityId=${studioId}` +
      `&timeRange=${range}`;
  } else {
    apiendpoint +=
      `?entityType=production` + `&entityId=${id}&timeRange=${range}`;
  }

  // if (productionId) {
  //   apiendpoint = apiendpoint + '&productionId=' + productionId
  // }

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setStudioSpendingGraph(data?.data));

        // cb(data.data)
      }

      // Stop loading indicator
      dispatch(setCommonDataLoader(false));
      dispatch(setSpendingGraphLoader(false));
    },
    (e: any) => {
      // Handle error, stop loading
      dispatch(setCommonDataLoader(false));
      dispatch(setSpendingGraphLoader(false));
    },
  );
};

export const getAllStudiosList = (
  query = '?page=1&limit=12',
  cb?: any,
  setHasMore?: any,
  isModal?: boolean | any,
  isStudio?: boolean,
) => {
  const { dispatch, getState } = store;
  dispatch(setCommonDataLoader(true));

  const { allStudiolist } = getState().studio;

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${endPoints.studio.AllStudiolist}${query}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setCommonDataLoader(false));
        const currentData = data.data.studios;
        if (isModal) {
          cb(currentData);

          // Determine if there is more data
          if (setHasMore) {
            const hasMore = currentData.length === 12; // If less than 10, no more data
            setHasMore(hasMore);
          }
          // cb((prev: any) => [...prev, ...data.data.data])
        } else {
          const updatedStudios = allStudiolist?.studios
            ? [...allStudiolist.studios, ...data.data.studios]
            : data.data.studios;

          dispatch(
            setAllStudioLists({
              ...data.data,
              studios: updatedStudios,
            }),
          );

          if (cb) {
            isStudio ? cb(currentData.length === 12) : cb(data.data.studios);
          }
          // cb && cb(data.data.studios);
        }

        // cb(data?.data?.depts)
      }
      // Stop loading indicator
      // dispatch(setCommonDataLoader(false))
    },
    (e: any) => {
      dispatch(setCommonDataLoader(false));
      // Handle error, stop loading
      if (cb) cb([]); // Pass empty array on error
      if (setHasMore) setHasMore(false);
      // dispatch(setCommonDataLoader(false))
    },
  );
};

export const getUserDetailsWithPhone = (values?: any, callback?: any) => {
  const { phoneNo, countryCode = '+1', countryAbbreviation } = values || {};

  const encodedSearchTerm = encodeURIComponent(countryCode);

  return (dispatch: any, getState: any) => {
    // dispatch(setCommonDataLoader({ loading: true }))
    getApiCall(
      `${endPoints.studio.checkUserwithPhoneNumber}?phoneNo=${phoneNo}&countryAbbreviation=${countryAbbreviation}&countryCode=${encodedSearchTerm}`,

      (s: any) => {
        if (s.data.statusCode === 400) {
          notify(s.data.message, 'error');
          return;
        }
        const {
          data: {
            data: { firstName, lastName, _id, message },
            // statusCode,
            // message,
          },
          data,
        } = s;

        // console.log('RES Products =>', firstName, lastName)
        if (firstName && lastName && _id) {
          callback(data);
        } else {
          callback({ message: message });
        }
        // dispatch(setCommonDataLoader({ loading: false }))
      },
      (e: any) => {
        // dispatch(setCommonDataLoader({ loading: false }))
        if (e?.data && e?.data.message) {
          console.log(e?.data.message);
          // notify(e?.data.message, 'error')
        } else {
          // notify(null, 'error')
        }
      },
    );
  };
};

export const addStudio = (values: any, callback: any) => {
  return (dispatch: any, getState: () => RootState) => {
    // const payload = {
    //   userType: userType,
    //   status: status,
    // }
    // dispatch(getProductionList({ loading: true }))

    postApiCall(
      endPoints.studio.createStudio,
      values,

      (s: any) => {
        const {
          data: { statusCode, data, message },
        } = s;
        if (statusCode === 400 && message) {
          notify(message, 'error');
          return;
        }
        if (statusCode && statusCode === 200) {
          notify('Studio created successfully.', 'success');
          // dispatch(getProductionList())
          callback(data);
        }
        // dispatch(getProductionList({ loading: false }))

        // dispatch(updateVendorManagement(false))
      },
      (e: any) => {
        //   setLoad(false)
        // dispatch(updateVendorManagement(false))
        // dispatch(getProductionList({ loading: false }))

        if (e?.data && e?.data.message) {
          notify(e?.data.message, 'error');
        }
      },
    );
  };
};

export const getSpendingProductionLists = (studioId: string, cb?: any) => {
  const { dispatch } = store;
  dispatch(setCommonDataLoader(true));
  dispatch(setSpendingGraphLoader(true));

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${endPoints.studio.spendingProductions}?studioId=${studioId}`;
  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        // Check the status value and dispatch the appropriate action
        dispatch(
          setAllStudioSpendingList([
            { _id: 'allProductions', name: 'All Projects' },
            ...data?.data,
          ]),
        );
        // console.log('=>>>>>>>>>>>>>>>>>>>', data)

        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setCommonDataLoader(false));
      dispatch(setSpendingGraphLoader(false));
    },
    (e: any) => {
      // Handle error, stop loading
      dispatch(setCommonDataLoader(false));
      dispatch(setSpendingGraphLoader(false));
    },
  );
};

export const uniqueStudioCheckMethod = (studioName: string, cb?: any) => {
  const { dispatch } = store;

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${endPoints.production.checkStudioUniqueName}?name=${studioName}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      console.log('S', s);
      const {
        data: { statusCode, message },
      } = s;
      if (statusCode) {
        // Check the status value and dispatch the appropriate action
        // Append productionId to the endpoint if provided
        if (statusCode === 400) {
          cb(message);
        }
      }

      // Stop loading indicator
      // dispatch(setCommonDataLoader(false))
      // dispatch(setSpendingGraphLoader(false))
    },
    (e: any) => {
      // Dispatch the retrieved studio spending graph data
      // Handle error, stop loading
      // dispatch(setCommonDataLoader(false))
      // dispatch(setSpendingGraphLoader(false))
      // Execute callback with the retrieved data
    },
  );
}; // Stop loading indicators
// Handle error, stop loading indicators

export const getStudioLocationList = (studioId: string, cb?: any) => {
  const { dispatch } = store;

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${endPoints.studio.studioLocations}?studioId=${studioId}`;

  // Make the API call
  getApiCall(
    apiendpoint,

    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setStudioLocation(data?.data));
      }
    },
    (e: any) => {
      // Dispatch the retrieved studio spending graph data
      // Handle error, stop loading
      // dispatch(setCommonDataLoader(false))
      // dispatch(setSpendingGraphLoader(false))
      // Execute callback with the retrieved data
    },
  );
}; // Stop loading indicators
// Handle error, stop loading indicators

export const getStudioProductionLocations = (studioId: string, cb?: any) => {
  const { dispatch } = store;

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${endPoints.studio.activestudiolocations}?studioId=${studioId}`;

  // Make the API call
  getApiCall(
    apiendpoint,

    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setStudioProductionLocation(data?.data));
      }
    },
    (e: any) => {
      // Dispatch the retrieved studio spending graph data
      // Handle error, stop loading
      // dispatch(setCommonDataLoader(false))
      // dispatch(setSpendingGraphLoader(false))
      // Execute callback with the retrieved data
    },
  );
};

export const getStudioDetail = (selectedstudioId: any, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${endPoints.studio.studioDetail}/${selectedstudioId}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setStudioDetail(data.data));
        // Callback after dispatch
        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    (e: any) => {
      // Handle error, stop loading
      // console.log(first)
      dispatch(setLoading(false));
    },
  );
};

//----------------------- -- -- - - -- -Code for optimise the api with promise.all method. --- -- -- -- -- - -- - - -- - -- -- -

// export const multiGetApiCallWithDispatch = async (
//   calls: ApiCallItem[]
// ): Promise<void> => {
//   const { dispatch } = store;

//   const promises = calls.map(
//     ({ url, action }) =>
//       new Promise((resolve, reject) => {
//         getApiCall(
//           url,
//           (res: any) => {
//             dispatch(action(res.data?.data));
//             resolve(res);
//           },
//           (err: any) => reject(err)
//         );
//       })
//   );

//   return Promise.all(promises).then(() => {});
// };

export const fetchStudioMetaData = async (studioId: string) => {
  const { dispatch } = store;

  dispatch(setLoading(true));

  const calls: any = [
    {
      url: `${endPoints.studio.studioDetail}/${studioId}`,
      dispatchFn: setStudioDetail,
    },
    {
      url: `${endPoints.studio.activestudiolocations}?studioId=${studioId}`,
      dispatchFn: setStudioProductionLocation,
    },
    {
      url: `${endPoints.studio.studioLocations}?studioId=${studioId}`,
      dispatchFn: setStudioLocation,
    },
    {
      url: `${endPoints.studio.spendingProductions}?studioId=${studioId}`,
      transformFn: (data: any) => [
        { _id: 'allProductions', name: 'All Projects' },
        ...data,
      ],
      dispatchFn: setAllStudioSpendingList,
    },
  ];

  await multiGetApiCallWithDispatch(calls, dispatch)
    .catch(err => {
      console.error('Error in multi API dispatch:', err);
    })
    .finally(() => {
      dispatch(setLoading(false));
    });
};

export const updateStudio = (id: string, values: any, callback: any) => {
  return (dispatch: any, getState: () => RootState) => {
    // const payload = {
    //   userType: userType,
    //   status: status,
    // }
    // dispatch(getProductionList({ loading: true }))

    putApiCall(
      `${endPoints.studio.updateStudio}/${id}`,
      values,

      (s: any) => {
        const {
          data: { statusCode, data, message },
        } = s;
        if (statusCode === 400 && message) {
          notify(message, 'error');
          return;
        }
        if (statusCode && statusCode === 200) {
          notify('Studio updated successfully.', 'success');
          // dispatch(getProductionList())
          callback(data);
        }
        // dispatch(getProductionList({ loading: false }))

        // dispatch(updateVendorManagement(false))
      },
      (e: any) => {
        //   setLoad(false)
        // dispatch(updateVendorManagement(false))
        // dispatch(getProductionList({ loading: false }))

        if (e?.data && e?.data.message) {
          notify(e?.data.message, 'error');
        }
      },
    );
  };
};

export const getStudioOrders = (
  selectedstudioId: any,
  cb?: any,
  modelEntityType?: string,
  modelEntityId?: string,
) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  // Build the API endpoint with the query and studioId
  const apiendpoint = `${
    endPoints.order.orderList
  }?entityId=${selectedstudioId}&entityType=studios&pageNo=1&limit=10${
    modelEntityType ? `&modelEntityType=${modelEntityType}` : ''
  }${modelEntityId ? `&modelEntityId=${modelEntityId}` : ''}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setStudioOrders(data.data));
        // Callback after dispatch
        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    (e: any) => {
      // Handle error, stop loading
      // console.log(first)
      dispatch(setLoading(false));
    },
  );
};

export const updateStudioFormDetails = (data: any, cb?: any) => {
  const { dispatch } = store;

  const contacts = store.getState().studio.contacts;

  dispatch(setStudioFormDetails(data));
  if (cb) cb(data);
};

export const updateContactsList = (data: any) => {
  const { dispatch } = store;

  const contacts = store.getState().studio.contacts;

  dispatch(setStudioContactsList([...contacts, data]));
};

export const removeContactsList = (data: any) => {
  const { dispatch } = store;

  // const contacts = store.getState().studio.contacts

  // console.log(contacts)

  dispatch(setStudioContactsList(data));
};

export const getEntityDeps = (entityType?: string, entityId?: string) => {
  const { dispatch } = store;

  let endpoint = `${endPoints.studio.getEntityDeps}?entityType=${entityType}`;

  if (entityId) {
    endpoint += `&entityId=${entityId}`;
  }

  getApiCall(
    endpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setEntityDeps(data.data));
      }

      // dispatch(setLoading(false));
    },
    (e: any) => {
      // dispatch(setLoading(false));
    },
  );
};

export const getStudioRoleList = (departmentId: string, entityType: string) => {
  const { dispatch } = store;

  // dispatch(setLoading(true));

  getApiCall(
    endPoints.studio.getStudioRoleList +
      `?departmentId=${departmentId}&entityType=${entityType}`,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setStudioRoleList(data.data));
      }

      // dispatch(setLoading(false));
    },
    (e: any) => {
      // dispatch(setLoading(false));
    },
  );
};

export const fetchRoleDetails = (roleId: string) => {
  const { dispatch } = store;

  // dispatch(setLoading(true));

  getApiCall(
    `${endPoints.studio.getRoleDetails}/${roleId}`,
    (s: any) => {
      const { data } = s;

      if (data.data) {
        dispatch(setRoleDetails(data?.data[0]));
      }

      // dispatch(setLoading(false));
    },
    (e: any) => {
      // dispatch(setLoading(false));
    },
  );
};

export const addContact = (
  data: any,
  navigate?: any,
  cb?: any,
  isStudio?: boolean,
) => {
  const { dispatch } = store;
  const entityType = data?.entity;

  dispatch(setLoading(true));

  postApiCall(
    endPoints.studio.addContact,
    data,
    (s: any) => {
      const { data } = s;

      if (data.data) {
        dispatch(setRoleDetails(data?.data[0]));
        if (!isStudio) {
          notify(data?.message, 'success');
        }

        try {
          logEvent(analytics, ANALYTICS_EVENTS.CONTACT_INVITED, {
            entity_type: entityType,
          });
        } catch (_) {}
        navigate(-1);
        if (cb) cb(data?.data);
      }

      dispatch(setLoading(false));
    },
    (e: any) => {
      notify(e?.data?.message, 'error');
      dispatch(setLoading(false));
    },
  );
};

export const fetchStaticRoleDetails = (
  accessLevel: string,
  permissionType: string,
  entity: string,
  cb?: any,
) => {
  const { dispatch } = store;

  // dispatch(setLoading(true));

  getApiCall(
    `${endPoints.studio.staticRoles}?accessLevel=${accessLevel}&permissionType=${permissionType}&entity=${entity}`,
    (s: any) => {
      const { data } = s;

      if (data.data) {
        if (!cb) dispatch(setRoleDetails(data?.data[0]));
        if (cb) cb(data?.data[0]);
      }

      // dispatch(setLoading(false));
    },
    (e: any) => {
      // dispatch(setLoading(false));
    },
  );
};

export const getEntityCount = (entityType: string, entityId: string) => {
  const { dispatch } = store;

  // dispatch(setLoading(true));

  getApiCall(
    endPoints.studio.getEntityCount +
      `?entityType=${entityType}&entityId=${entityId}`,
    (s: any) => {
      const { data } = s;

      if (data?.data) {
        dispatch(setEntityCount(data?.data));
      }

      // dispatch(setLoading(false));
    },
    (e: any) => {
      // dispatch(setLoading(false));
    },
  );
};

export const addRole = (data: any, setValue: any, cb?: any) => {
  const { dispatch } = store;

  // dispatch(setLoading(true));

  postApiCall(
    endPoints.studio.addRole,
    data,
    (s: any) => {
      const { data } = s;

      if (data.data) {
        const newRoleOption: any = {
          roleName: data?.data?.roleName,
          isNew: false,
          _id: data?.data?._id,
        };

        setValue(
          'roleName',
          newRoleOption,
          {
            shouldValidate: true,
            shouldDirty: true,
          },
          { shouldValidate: true },
        );
        if (cb) cb();
        notify(data?.data?.message, 'success');
      }

      // dispatch(setLoading(false));
    },
    (e: any) => {
      notify(e?.data?.message, 'error');
      // dispatch(setLoading(false));
    },
  );
};

export const getContactDetails = (memberId: string, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  const apiendpoint = `${endPoints.updateCrewMember}${memberId}?memberType=CONTACT`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        // Check the status value and dispatch the appropriate action
        console.log('data==========>>>>>>>>>>>', data);
        dispatch(setContactDetails(data?.data));

        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    (e: any) => {
      // Handle error, stop loading
      dispatch(setLoading(false));
    },
  );
};

export const checkUniqueMember = (payload: any, cb?: any) => {
  const { dispatch } = store;
  // dispatch(setLoading(true));

  postApiCall(
    endPoints.studio.checkUniqueMember,
    payload,
    (s: any) => {
      // dispatch(setLoading(false));

      const response = s?.data;

      // 🔴 BUSINESS FAILURE (not unique)
      if (response?.type === 'MEMBER_ALREADY_EXIST') {
        cb?.({
          success: false,
          type: response.type,
          message: response.message,
        });
        return;
      }

      // ✅ SUCCESS (unique member)
      cb?.({
        success: true,
        data: response?.data,
      });
    },
    (e: any) => {
      // dispatch(setLoading(false));

      cb?.({
        success: false,
        message: 'Something went wrong. Please try again.',
      });
    },
  );
};

export const updateContact = (
  data: any,
  id: string,
  navigate?: any,
  cb?: any,
) => {
  const { dispatch } = store;

  dispatch(setLoading(true));

  putApiCall(
    `${endPoints.studio.updateContacts}${id}`,
    data,
    (s: any) => {
      const { data } = s;

      if (data.data) {
        // dispatch(setRoleDetails(data?.data[0]));
        notify(data?.message, 'success');
        navigate(-1);
        if (cb) cb(data?.data);
      }

      dispatch(setLoading(false));
    },
    (e: any) => {
      notify(e?.data?.message, 'error');
      dispatch(setLoading(false));
    },
  );
};
